import React from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import styled from 'styled-components';

import { Box, Display, Flex, Heading, Paragraph } from '@rover/kibble/core';
import MQ from '@rover/kibble/styles/MediaQuery';
import useTheme from '@rover/kibble/themes/useTheme';
import PageLayout from '@rover/react-lib/src/components/layouts/PageLayout';
import sitterDesktop from '@rover/react-lib/src/images/policies/guarantee/rg_lp_photo_02_desktop.jpg';
import sitterMobile from '@rover/react-lib/src/images/policies/guarantee/rg_lp_photo_02_mobile.jpg';
import sitterTablet from '@rover/react-lib/src/images/policies/guarantee/rg_lp_photo_02_tablet.jpg';
import starsIconLeft from '@rover/react-lib/src/images/policies/guarantee/stars_left_01.svg?url';
import inject from '@rover/utilities/injector';

import { ParagraphBrand2StyledLink, StyledBulletedList } from '../brand2ColorStyles.constants';

import Ellipse from './images/ellipse.svg';
import MultiImage, { Coordinates } from './MultiImage';

const { guaranteeTosUrl, minimumContribution } = inject('Rover.context', {});
const ResponsivePageLayout = styled(PageLayout)`
  margin-bottom: ${({ theme }) => theme.space['24x']};
  ${MQ.XS.toString()} {
    margin-bottom: ${({ theme }) => theme.space['16x']};
  }
  margin-top: ${({ theme }) => theme.space['0x']};
`;

const getList = () => {
  return [
    {
      IconClass: Ellipse,
      title: t({
        message: `Rover will reimburse costs after the first ${minimumContribution}`,
        comment: '{minimumContribution} is the value of the minimum contribution',
      }),
      content: '',
    },
    {
      IconClass: Ellipse,
      title: t`Care is covered for up to 30 days.`,
      content: '',
    },
  ].map((item, index) => ({ ...item, key: index, hasBorder: false }));
};

const defaultCoordinates: Coordinates = {
  height: '200px',
  width: '200px',
};
const tabletCoordinates: Coordinates = {
  height: '150px',
  width: '150px',
};
const mobileCoordinates: Coordinates = {
  height: '150px',
  width: '150px',
};

const WhoIsRoverForSection = (): JSX.Element => {
  const { text, space } = useTheme();

  return (
    <Flex justifyContent="center" data-testid="who-is-rover-for-section">
      <ResponsivePageLayout>
        <Flex flexWrap="wrap" gap="12x">
          <Box>
            <MultiImage
              defaultImageSrc={sitterDesktop}
              tabletImageSrc={sitterTablet}
              mobileImageSrc={sitterMobile}
              additionalImage={starsIconLeft}
              defaultCoordinates={defaultCoordinates}
              tabletCoordinates={tabletCoordinates}
              mobileCoordinates={mobileCoordinates}
            />
          </Box>
          <Box flex="1 0 40%">
            <Display size="500">
              <Trans>Who is the Rover Guarantee for? Pets!</Trans>
            </Display>

            <Paragraph textColor="primary" webProps={{ maxWidth: 'none' }} pb="6x" size="200">
              <Trans>
                The Rover Guarantee is a reimbursement program primarily intended to protect pets.
                In certain circumstances, it can also cover vet care for a pet care provider’s pets
                or damage to a pet owner’s property.
              </Trans>
            </Paragraph>

            <Heading textColor="primary" size="300">
              <Trans>What to expect</Trans>
            </Heading>

            <StyledBulletedList
              fontSize={text['200'].fontSize}
              height={space['6x']}
              items={getList()}
            />

            <Box mt="4x">
              <a href={guaranteeTosUrl}>
                <ParagraphBrand2StyledLink size="200">
                  <Trans>See Rover Guarantee Terms for more details</Trans>
                </ParagraphBrand2StyledLink>
              </a>
            </Box>
          </Box>
        </Flex>
      </ResponsivePageLayout>
    </Flex>
  );
};

export default WhoIsRoverForSection;
